import { Link, createFileRoute } from '@tanstack/react-router'
import { fallback, zodValidator } from '@tanstack/zod-adapter'
import dayjs from 'dayjs'
import React, { useMemo } from 'react'
import { z } from 'zod'
import OrderListAndTrackingFilter from './-components/filter'
import type { BadgeVariantType } from '@/components/ui/badge'
import type { AccessorColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/data-table'
import PageContainer from '@/components/page-container'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import { ButtonGroup } from '@/components/ui/button-group'
import {
  RtlsEye,
  RtlsFileExport,
  RtlsFileImport,
  RtlsPen,
  RtlsReceipt,
  RtlsTrashAlt,
} from '@/icons'
import { currencyFormat } from '@/lib/currencyFormatter'
import { buildQueryParams } from '@/lib/utils'
import $api from '@/services/api'

export const orderStatusFilterOptions = [
  'Processing',
  'In Transit',
  'At Sorting Facility',
  'On Process',
  'Delivered (Complete)',
  'Cancelled',
  'Failed',
  'Returned to Sorting Facility',
] as const

export type OrderStatus = (typeof orderStatusFilterOptions)[number]
export const orderListSearchSchema = z.object({
  page: fallback(z.string(), '1'),
  limit: fallback(z.string(), '10'),
  keyword: fallback(z.string(), ''),
  orderDate: fallback(z.string(), ''),
  orderTypeId: fallback(z.string(), ''),
  customerId: fallback(z.string(), ''),
  status: fallback(z.enum([...orderStatusFilterOptions, '']), ''),
})

export type OrderListSearch = z.input<typeof orderListSearchSchema>

export const Route = createFileRoute('/admin/order-list-and-tracking/')({
  validateSearch: zodValidator({
    schema: orderListSearchSchema,
    input: 'input',
    output: 'output',
  }),
  component: OrdersAndTracking,
})

function OrdersAndTracking() {
  const searchParams = Route.useSearch()
  const page = searchParams.page
  const limit = searchParams.limit

  const navigate = Route.useNavigate()

  const { data, isPending } = $api.useQuery('get', '/orders', {
    params: {
      query: buildQueryParams(searchParams),
    },
  })

  const dataOrders = useMemo(() => {
    if (!data?.items) return []
    return data.items.map((order, index) => ({
      key: order.id,
      no: (Number(page) - 1) * Number(limit) + index + 1,
      orderId: order.id,
      hawb: order.hawb,
      orderDate: order.createdAt,
      status: order.status as unknown as OrderStatus,
      collectionDate: order.collectionDate,
      deliveryDate: order.deliveryDate,
      senderName:
        order.pickupToggle === true ? order.pickupPerson : order.customerName,
      email: order.customerEmail,
      pickupAddress: order.deliveryAddress,
      address: order.recipientAddress,
      recipientName: order.recipientName,
      weight: `${order.weight} Kg`,
      price: currencyFormat(order.price),
      bags: order.bags.map((bag) => ({
        id: bag.id,
        hawb: bag.hawb,
      })),
      batchHawb: order.bags.length > 0 ? order.bags[0]?.batchHawb : null,
    }))
  }, [data, page, limit])

  const columns = React.useMemo<
    Array<AccessorColumnDef<(typeof dataOrders)[number], any>>
  >(
    () => [
      {
        accessorKey: 'no',
        header: 'NO',
        size: 80,
        meta: {
          cellClassName: 'text-center',
        },
      },
      {
        accessorKey: 'hawb',
        header: 'ORDER ID',
        meta: {
          cellClassName: 'font-semibold',
        },
      },
      {
        accessorKey: 'orderDate',
        header: 'ORDER DATE',
        size: 160,
        cell: (info) => {
          const value = info.getValue()
          return value ? dayjs(value).format('dd/MM/yyyy') : '-'
        },
      },
      {
        accessorKey: 'status',
        header: 'STATUS',
        size: 140,
        cell: (info) => {
          const value = info.getValue() as (typeof dataOrders)[number]['status']
          let type = 'default' as BadgeVariantType
          switch (value) {
            case 'Processing':
            case 'On Process':
            case 'In Transit':
            case 'At Sorting Facility':
              type = 'info'
              break
            case 'Delivered (Complete)':
              type = 'success'
              break
            case 'Cancelled':
            case 'Failed':
              type = 'error'
              break
            case 'Returned to Sorting Facility':
              type = 'warning'
              break
          }
          return (
            <Badge variant="outline" type={type} className="font-semibold">
              {value}
            </Badge>
          )
        },
      },
      {
        accessorKey: 'batchHawb',
        header: 'BATCH',
        size: 180,
        cell: (info) => info.getValue() || '-',
        meta: {
          cellClassName: 'font-semibold',
        },
      },
      {
        accessorKey: 'bags',
        header: 'BAG',
        size: 120,
        cell: (info) => {
          const value = info.getValue() as (typeof dataOrders)[number]['bags']
          return value.length === 0 ? (
            '-'
          ) : (
            <div className="space-y-1 ">
              {value.map((bag) => (
                <span key={bag.id} className="font-semibold block">
                  {bag.hawb || '-'}
                </span>
              ))}
            </div>
          )
        },
      },
      {
        accessorKey: 'collectionDate',
        header: 'COLLECTION DATE',
        size: 180,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return 'n/a'
          return (
            <div className="flex items-center">
              <span className="font-bold bg-neutral-20 rounded-sm text-neutral-90 size-6 inline-flex justify-center items-center mr-2">
                C
              </span>
              <span className="mr-1">{dayjs(value).format('dd/MM/yyyy')},</span>
              <span className="font-bold">{dayjs(value).format('HH:mm')}</span>
            </div>
          )
        },
      },
      {
        accessorKey: 'deliveryDate',
        header: 'DELIVERY DATE',
        size: 180,
        cell: (info) => {
          const value = info.getValue()
          if (!value) return 'n/a'
          return (
            <div className="flex items-center">
              <span className="font-bold bg-neutral-20 rounded-sm text-neutral-90 size-6 inline-flex justify-center items-center mr-2">
                D
              </span>
              <span className="mr-1">{dayjs(value).format('dd/MM/yyyy')},</span>
              <span className="font-bold">{dayjs(value).format('HH:mm')}</span>
            </div>
          )
        },
      },
      {
        accessorKey: 'senderName',
        header: 'SENDER NAME',
        size: 280,
        cell: (info) => {
          const value = info.getValue()
          const record = info.row.original
          return (
            <div className="flex items-center gap-3">
              <Avatar className="size-10">
                <AvatarFallback className="font-semibold text-neutral-90 text-lg bg-neutral-20">
                  {typeof value === 'string' && value.length > 0
                    ? value.charAt(0)
                    : '?'}
                </AvatarFallback>
              </Avatar>
              <div>
                <span className="block font-semibold leading-none">
                  {value}
                </span>
                <span className="text-xs leading-none">{record.email}</span>
              </div>
            </div>
          )
        },
      },
      {
        accessorKey: 'address',
        header: 'ADDRESS',
        size: 300,
        cell: (info) => {
          const value = info.getValue()
          const record = info.row.original
          if (record.pickupAddress && record.pickupAddress.length > 0) {
            return (
              <div className="whitespace-pre-line">
                {`Pickup Address:\n${record.pickupAddress}\n\nDelivery Address:\n${value}`}
              </div>
            )
          }
          return value
        },
      },
      {
        accessorKey: 'recipientName',
        header: 'RECIPIENT NAME',
        size: 210,
        meta: {
          cellClassName: 'font-semibold',
        },
      },
      {
        accessorKey: 'weight',
        header: 'WEIGHT',
        size: 100,
        meta: {
          cellClassName: 'font-semibold',
        },
      },
      {
        header: 'PRICE',
        accessorKey: 'price',
        size: 120,
        meta: {
          cellClassName: 'font-semibold',
        },
      },
      {
        accessorKey: 'orderId',
        header: 'HISTORY',
        size: 90,
        cell: () => {
          return (
            <Link to="/" className="underline font-semibold text-primary">
              Detail
            </Link>
          )
        },
        meta: {
          cellClassName: 'text-center',
        },
      },
      {
        id: 'actions',
        accessorKey: 'key',
        header: 'ACTION',
        meta: {
          headerClassName: 'text-center',
        },
        cell: () => {
          return (
            <ButtonGroup>
              <Link
                to="/"
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                <RtlsEye className="text-primary size-3.5" />
              </Link>
              <Link
                to="/"
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                <RtlsPen className="text-primary size-3.5" />
              </Link>
              <Link
                target="_black"
                to="/"
                className={buttonVariants({ variant: 'outline', size: 'sm' })}
              >
                <RtlsReceipt className="size-3.5" />
              </Link>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  // confirm({
                  //   title: 'Are you sure want to Delete selected item?',
                  //   onOk() {
                  //     deleteMutation.mutate(r.orderId)
                  //   },
                  // })
                }}
              >
                <RtlsTrashAlt className="size-3.5 text-neutral-60" />
              </Button>
            </ButtonGroup>
          )
        },
      },
    ],
    [],
  )

  const actions = React.useMemo(
    () => [
      <Button key="import" variant="outline" className="w-full sm:w-auto">
        <RtlsFileImport />
        Import Data
      </Button>,
      <Button key="export" variant="outline" className="w-full sm:w-auto">
        <RtlsFileExport />
        Export Data
      </Button>,
      <Button key="create" className="w-full sm:w-auto">
        Crete Order Form
      </Button>,
    ],
    [],
  )
  return (
    <PageContainer title="Order List & Tracking" actions={actions}>
      <OrderListAndTrackingFilter disabled={isPending} />
      <DataTable
        columns={columns}
        loading={false}
        data={dataOrders}
        columnPinning={{
          right: ['actions'],
        }}
        pagination={{
          page: Number(page),
          pageSize: Number(limit),
          total: data?.meta.total ?? 0,
          onChange: (updater) => {
            navigate({
              search: (s) => ({
                ...s,
                page: `${updater.page}`,
                limit: `${updater.pageSize}`,
              }),
            })
          },
        }}
      />
    </PageContainer>
  )
}
